/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { notifyManager, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AUTH_UNAUTHORIZED_EVENT,
  api,
  toApiError,
  type ApiErrorShape,
} from '@/api/client';
import { queryKeys } from '@/api/queryClient';
import type { components } from '@/api/schema';

type UserProfile = components['schemas']['UserProfileResponse'];
type LoginInput = components['schemas']['LoginRequest'];
type RegisterInput = components['schemas']['RegisterRequest'];
type VerificationInput = { email: string; code: string } | { token: string };
type PasswordResetInput =
  | { email: string; code: string; newPassword: string }
  | { token: string; newPassword: string };

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export class EmailNotVerifiedError extends Error {
  readonly code = 'EMAIL_NOT_VERIFIED';
  constructor() {
    super('Email not verified');
    this.name = 'EmailNotVerifiedError';
  }
}

interface AuthContextValue {
  status: AuthStatus;
  user: UserProfile | null;
  login: (input: LoginInput) => Promise<UserProfile>;
  register: (input: RegisterInput) => Promise<void>;
  verifyEmail: (input: VerificationInput) => Promise<UserProfile>;
  resendVerification: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<UserProfile | null>;
  changePassword: (input: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  completePasswordReset: (input: PasswordResetInput) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchCurrentUser(): Promise<UserProfile> {
  const { data, error, response } = await api.GET('/api/v1/users/me');
  if (error || !data) {
    throw toApiError(error, response.status);
  }
  return data;
}

// Query Client cache writes (fetchQuery/resetQueries/setQueryData) notify
// mounted `useQuery` observers through notifyManager, which defers the
// actual re-render to a macrotask (it schedules via setTimeout(0), not a
// microtask) — so a cache write is NOT yet visible to React the instant its
// promise resolves. Any caller that awaits a cache-mutating auth call and
// then immediately navigates (a route guard reads `status` synchronously on
// render) must await this afterward, or the guard sees the pre-mutation
// value and can bounce the user to the wrong place.
function waitForAuthSync(): Promise<void> {
  return new Promise((resolve) => notifyManager.schedule(resolve));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery<UserProfile, ApiErrorShape>({
    queryKey: queryKeys.me,
    queryFn: fetchCurrentUser,
    staleTime: 60_000,
    // The global default disables this, but a dead session is most often
    // discovered by coming back to a tab left open for hours — checking on
    // focus catches that without polling in the background the whole time.
    refetchOnWindowFocus: true,
    // Don't retry on 401 — it just means no active session.
    retry: (failureCount, err) => err?.status !== 401 && failureCount < 1,
  });

  // A 401 from any request is surfaced by the fetch middleware as a DOM event.
  // When the /me query is still cached (user looks authenticated) but a later
  // request 401s (cookie expired between requests), drop the cache immediately.
  // Use resetQueries (not removeQueries): removeQueries only evicts the cache
  // entry and cancels its retryer *silently*, so the still-mounted `me`
  // observer here never sees the change and keeps rendering the stale
  // authenticated user until a full reload. resetQueries dispatches a real
  // state change, so this component re-renders with `data: undefined` (soon
  // after — see waitForAuthSync) and `RequireAuth` can redirect without a
  // reload. Nothing here awaits that render, which is fine: unlike
  // login/changePassword/etc., there's no immediate follow-up navigation to
  // race against — RequireAuth just reacts whenever the render lands.
  useEffect(() => {
    const onUnauthorized = () => {
      if (meQuery.data) {
        void queryClient.resetQueries({ queryKey: queryKeys.me });
      }
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () =>
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, [meQuery.data, queryClient]);

  const loadMe = useCallback(
    async (): Promise<UserProfile> => {
      const data = await queryClient.fetchQuery<UserProfile, ApiErrorShape>({
        queryKey: queryKeys.me,
        queryFn: fetchCurrentUser,
      });
      // Callers (login/verifyEmail) navigate right after this
      // resolves; see waitForAuthSync for why this is required.
      await waitForAuthSync();
      return data;
    },
    [queryClient],
  );

  const login = useCallback(
    async (input: LoginInput): Promise<UserProfile> => {
      const { data, error, response } = await api.POST('/api/v1/auth/login', {
        body: input,
      });
      if (error || !data) {
        const apiErr = toApiError(error, response.status);
        if (response.status === 403) throw new EmailNotVerifiedError();
        throw apiErr;
      }
      // Cookie was set by the server; just load the profile.
      return loadMe();
    },
    [loadMe],
  );

  const register = useCallback(async (input: RegisterInput): Promise<void> => {
    const { error, response } = await api.POST('/api/v1/auth/register', {
      body: input,
    });
    if (error) throw toApiError(error, response.status);
  }, []);

  const verifyEmail = useCallback(
    async (input: VerificationInput): Promise<UserProfile> => {
      const { data, error, response } = await api.POST(
        '/api/v1/auth/verify-email',
        {
          body: input,
        },
      );
      if (error || !data) throw toApiError(error, response.status);
      return loadMe();
    },
    [loadMe],
  );

  const resendVerification = useCallback(
    async (email: string): Promise<void> => {
      const { error, response } = await api.POST(
        '/api/v1/auth/resend-verification',
        {
          body: { email },
        },
      );
      if (error) throw toApiError(error, response.status);
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    // Best-effort: server clears cookies and bumps tokenVersion.
    // Even if the request fails we clear local cache so the UI returns to anonymous.
    try {
      await api.POST('/api/v1/auth/logout');
    } finally {
      queryClient.clear();
    }
  }, [queryClient]);

  const refreshUser = useCallback(async (): Promise<UserProfile | null> => {
    if (!meQuery.data) return null;
    try {
      return await loadMe();
    } catch {
      return null;
    }
  }, [meQuery.data, loadMe]);

  const changePassword = useCallback(
    async (input: {
      currentPassword: string;
      newPassword: string;
    }): Promise<void> => {
      const { error, response } = await api.POST(
        '/api/v1/auth/password/change',
        { body: input },
      );
      if (error) throw toApiError(error, response.status);
      // Server cleared our cookies. Use resetQueries (not clear/removeQueries):
      // those evict the cache silently, so the still-mounted `me` observer
      // never sees the change and `status` stays 'authenticated' until a full
      // reload — a caller that navigates to /login right after this resolves
      // would get bounced back by LoginPage's authenticated-redirect guard.
      // Also wait for the flush itself (see waitForAuthSync) — resetQueries
      // resolving doesn't by itself mean React has re-rendered yet.
      await queryClient.resetQueries({ queryKey: queryKeys.me });
      await waitForAuthSync();
    },
    [queryClient],
  );

  const requestPasswordReset = useCallback(
    async (email: string): Promise<void> => {
      const { error, response } = await api.POST(
        '/api/v1/auth/password/forgot',
        { body: { email } },
      );
      if (error) throw toApiError(error, response.status);
    },
    [],
  );

  const completePasswordReset = useCallback(
    async (input: PasswordResetInput): Promise<void> => {
      const { error, response } = await api.POST(
        '/api/v1/auth/password/reset',
        { body: input },
      );
      if (error) throw toApiError(error, response.status);
      // See changePassword above: resetQueries + waitForAuthSync, so `status`
      // updates before a caller navigates away.
      await queryClient.resetQueries({ queryKey: queryKeys.me });
      await waitForAuthSync();
    },
    [queryClient],
  );

  const status: AuthStatus = useMemo(() => {
    if (meQuery.isLoading) return 'loading';
    if (meQuery.data) return 'authenticated';
    return 'anonymous';
  }, [meQuery.isLoading, meQuery.data]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: meQuery.data ?? null,
      login,
      register,
      verifyEmail,
      resendVerification,
      logout,
      refreshUser,
      changePassword,
      requestPasswordReset,
      completePasswordReset,
    }),
    [
      status,
      meQuery.data,
      login,
      register,
      verifyEmail,
      resendVerification,
      logout,
      refreshUser,
      changePassword,
      requestPasswordReset,
      completePasswordReset,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Non-null access to the current user — use inside components rendered under
 * `RequireAuth`, where `status === 'authenticated'` is guaranteed.
 */
export function useCurrentUser(): UserProfile {
  const { user } = useAuth();
  if (!user) {
    throw new Error(
      'useCurrentUser must be used inside an authenticated route',
    );
  }
  return user;
}
